import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Boxes, PackagePlus, Search } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { useStockQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import {
  Badge, Card, EmptyState, ErrorState, Input, LoadingState, PageHeader, Pagination,
  Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';

export function StockPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const format = useFormat();
  const warehouses = useAppSelector((state) => state.tenant.context?.warehouses ?? []);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const warehouseId = searchParams.get('warehouseId') ?? '';
  const lowStockOnly = searchParams.get('lowStockOnly') === 'true';
  const outOfStockOnly = searchParams.get('outOfStockOnly') === 'true';

  const { data, isLoading, isError, error, refetch, isFetching } = useStockQuery({
    page,
    limit,
    search: search || undefined,
    warehouseId: warehouseId || undefined,
    lowStockOnly: lowStockOnly || undefined,
    outOfStockOnly: outOfStockOnly || undefined,
  });

  const setFilter = (key: string, value: string | null): void => {
    setPage(1);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  const activeFilter = lowStockOnly ? 'low' : outOfStockOnly ? 'out' : 'all';

  return (
    <>
      <PageHeader
        title="Stock"
        description="Live balances per product, warehouse and batch — derived from the inventory ledger."
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Stock lines', value: String(data.summary.count), hint: 'product, warehouse, batch' },
            {
              label: 'On hand',
              value: formatQuantity(String(data.summary.quantity), format),
              hint: `${formatQuantity(String(data.summary.reserved), format)} reserved`,
            },
            {
              label: 'Low stock',
              value: String(data.summary.lowStock),
              hint: 'at or below reorder level',
            },
            {
              label: 'Out of stock',
              value: String(data.summary.outOfStock),
              hint: 'nothing left to sell',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-56 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Name, SKU or barcode"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="w-full sm:w-56">
            <Select
              label="Warehouse"
              value={warehouseId}
              onChange={(event) => setFilter('warehouseId', event.target.value || null)}
            >
              <option value="">All warehouses</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
              ))}
            </Select>
          </div>

          <div className="w-full sm:w-48">
            <Select
              label="Show"
              value={activeFilter}
              onChange={(event) => {
                const value = event.target.value;
                setFilter('lowStockOnly', value === 'low' ? 'true' : null);
                setFilter('outOfStockOnly', value === 'out' ? 'true' : null);
              }}
            >
              <option value="all">Everything</option>
              <option value="low">At or below reorder level</option>
              <option value="out">Out of stock</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState label="Loading stock…" />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title={search || activeFilter !== 'all' ? 'No stock matches those filters' : 'No stock recorded yet'}
            description={
              search || activeFilter !== 'all'
                ? 'Try widening your search or clearing the filters.'
                : 'Once products exist and opening balances are posted, live balances appear here.'
            }
          />
        ) : (
          <>
            <div aria-busy={isFetching}>
              <Table>
                <thead>
                  <tr>
                    <Th>Product</Th>
                    <Th>Warehouse</Th>
                    <Th>Batch</Th>
                    <Th numeric>On hand</Th>
                    <Th numeric>Reserved</Th>
                    <Th numeric>Available</Th>
                    <Can permission={P.PRODUCT_COST_VIEW}>
                      <Th numeric>Avg. cost</Th>
                      <Th numeric>Value</Th>
                    </Can>
                    <Th>Last movement</Th>
                    <Can permission={P.STOCK_ADJUST}>
                      <Th />
                    </Can>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((row) => (
                    <tr key={row.id} className="hover:bg-sunken">
                      <Td>
                        <p className="font-medium">{row.product.name}</p>
                        <p className="text-xs text-muted">
                          {row.variant.sku}
                          {row.product.category && ` · ${row.product.category.name}`}
                        </p>
                      </Td>
                      <Td className="text-secondary">{row.warehouse.name}</Td>
                      <Td className="text-secondary">
                        {row.batch ? (
                          <span className="font-mono text-xs">{row.batch.batchNumber}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </Td>
                      <Td numeric>
                        <span className="font-medium">
                          {formatQuantity(row.quantity, format)}
                        </span>{' '}
                        <span className="text-xs text-muted">{row.product.baseUnit.symbol}</span>
                      </Td>
                      <Td numeric className="text-secondary">
                        {formatQuantity(row.reservedQuantity, format)}
                      </Td>
                      <Td numeric>
                        {row.isOutOfStock ? (
                          <Badge tone="danger">Out of stock</Badge>
                        ) : row.isLowStock ? (
                          <Badge tone="warning">{formatQuantity(row.availableQuantity, format)}</Badge>
                        ) : (
                          formatQuantity(row.availableQuantity, format)
                        )}
                      </Td>
                      <Can permission={P.PRODUCT_COST_VIEW}>
                        <Td numeric className="text-secondary">{formatMoney(row.averageCost, format)}</Td>
                        <Td numeric className="font-medium">{formatMoney(row.stockValue, format)}</Td>
                      </Can>
                      <Td className="text-xs text-muted">
                        {row.lastMovementAt ? formatDateTime(row.lastMovementAt, format) : '—'}
                      </Td>
                      {/* Restocking starts from the row you are looking at, so the
                          product and warehouse travel with the link. */}
                      <Can permission={P.STOCK_ADJUST}>
                        <Td>
                          <Link
                            to={`/app/inventory/adjustments?reason=RESTOCK&variantId=${row.variant.id}&warehouseId=${row.warehouse.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1.5 text-xs font-medium text-secondary transition-colors hover:bg-sunken"
                          >
                            <PackagePlus className="size-3.5" aria-hidden />
                            Restock
                          </Link>
                        </Td>
                      </Can>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
                limit={limit}
                onLimitChange={(next) => {
                  setLimit(next);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
}
